import React from 'react';
import PropTypes from 'prop-types';
import { Grid, Card, CardContent, TextField, Button, Autocomplete } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';

const InventoryAssignmentFilters = ({
  searchTerm,
  employeeFilter,
  inventoryItemFilter,
  statusFilter,
  filterOptions,
  onSearchChange,
  onEmployeeFilterChange,
  onInventoryItemFilterChange,
  onStatusFilterChange,
  onClearFilters,
}) => {
  const defaultOption = { value: 'all', label: 'All' };

  const formatEmployeeLabel = (emp) => {
    if (emp?.full_name) {
      return emp.employee_id ? `${emp.full_name} (${emp.employee_id})` : emp.full_name;
    }
    return emp?.label || emp?.name || String(emp || '');
  };

  const employeeOptions = (filterOptions?.assigned_employees || []).map((emp) => ({
    value: emp?.id ?? emp?.value ?? emp,
    label: formatEmployeeLabel(emp),
  }));

  const inventoryItemOptions = (filterOptions?.inventory_items || []).map((item) => ({
    value: item?.id ?? item?.value ?? item,
    label: item?.name || item?.label || String(item || ''),
  }));

  const rawStatusOptions = (filterOptions?.return_status_options || []).map((status) => ({
    value: status?.value ?? status,
    label: status?.label || (typeof status === 'string' ? status : ''),
  }));

  const statusOptions = rawStatusOptions.length
    ? rawStatusOptions
    : [
        { value: 'returned', label: 'Returned' },
        { value: 'not_returned', label: 'Not Returned' },
      ];

  const withDefault = (options) =>
    options.some((option) => String(option.value) === 'all')
      ? options
      : [defaultOption, ...options];

  const employeeOptionsWithDefault = withDefault(employeeOptions);
  const inventoryItemOptionsWithDefault = withDefault(inventoryItemOptions);
  const statusOptionsWithDefault = withDefault(statusOptions);

  const getSelectedOption = (options, value) =>
    options.find((option) => String(option.value) === String(value)) || defaultOption;

  const selectedEmployee = getSelectedOption(employeeOptionsWithDefault, employeeFilter);
  const selectedInventoryItem = getSelectedOption(inventoryItemOptionsWithDefault, inventoryItemFilter);
  const selectedStatus = getSelectedOption(statusOptionsWithDefault, statusFilter);

  return (
    <Grid item xs={12}>
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={2.5}>
              <TextField
                fullWidth
                placeholder="Search assignments..."
                value={searchTerm}
                onChange={(e) => onSearchChange(e.target.value)}
                size="small"
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Autocomplete
                size="small"
                value={selectedEmployee}
                onChange={(event, newValue) => onEmployeeFilterChange(newValue?.value || 'all')}
                options={employeeOptionsWithDefault}
                getOptionLabel={(option) => option?.label || ''}
                isOptionEqualToValue={(option, value) => String(option?.value) === String(value?.value)}
                renderInput={(params) => <TextField {...params} label="Employee" />}
                clearOnEscape
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Autocomplete
                size="small"
                value={selectedInventoryItem}
                onChange={(event, newValue) => onInventoryItemFilterChange(newValue?.value || 'all')}
                options={inventoryItemOptionsWithDefault}
                getOptionLabel={(option) => option?.label || ''}
                isOptionEqualToValue={(option, value) => String(option?.value) === String(value?.value)}
                renderInput={(params) => <TextField {...params} label="Item" />}
                clearOnEscape
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Autocomplete
                size="small"
                value={selectedStatus}
                onChange={(event, newValue) => onStatusFilterChange(newValue?.value || 'all')}
                options={statusOptionsWithDefault}
                getOptionLabel={(option) => option?.label || ''}
                isOptionEqualToValue={(option, value) => String(option?.value) === String(value?.value)}
                renderInput={(params) => <TextField {...params} label="Status" />}
                clearOnEscape
              />
            </Grid>
            <Grid item xs={12} sm={6} md={1}>
              <Button variant="outlined" onClick={onClearFilters} size="small" sx={{ height: '40px' }}>
                Clear Filters
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Grid>
  );
};

InventoryAssignmentFilters.propTypes = {
  searchTerm: PropTypes.string.isRequired,
  employeeFilter: PropTypes.string.isRequired,
  inventoryItemFilter: PropTypes.string.isRequired,
  statusFilter: PropTypes.string.isRequired,
  filterOptions: PropTypes.shape({
    assigned_employees: PropTypes.arrayOf(PropTypes.object),
    inventory_items: PropTypes.arrayOf(PropTypes.object),
    return_status_options: PropTypes.arrayOf(PropTypes.oneOfType([
      PropTypes.shape({ value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]), label: PropTypes.string }),
      PropTypes.string,
    ])),
  }),
  onSearchChange: PropTypes.func.isRequired,
  onEmployeeFilterChange: PropTypes.func.isRequired,
  onInventoryItemFilterChange: PropTypes.func.isRequired,
  onStatusFilterChange: PropTypes.func.isRequired,
  onClearFilters: PropTypes.func.isRequired,
};

InventoryAssignmentFilters.defaultProps = {
  filterOptions: {},
};

export default InventoryAssignmentFilters;
